import React, { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { Controller, useFormContext } from 'react-hook-form';

import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import { uuidv4 } from 'common/uuidGenerator';
import { getPresignedURLOpen } from 'services/Settings';
import { axiosInstance } from 'common/apiHandler';
import { useNavbarContext } from 'context/NavbarContext';
import { getSignedUrl } from 'modules/file_manager/services';
import LabelName from 'components/UI/LabelName/LabelName';
import SubText from '../FormComponenets/SubText';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

function FileUpload({ handleSettingsClick, item, index, isFormBuilder, isFromPreview = false }) {
  const {
    control,
    setError,
    clearErrors,
    formState: { errors },
    getValues,
  } = useFormContext();

  const { downloadFile } = useNavbarContext();
  const { addToast } = useToast();

  const { subtext, required, label = 'Short Text' } = item || {};
  const name = `form_data.${index}.value`;
  const [uploadingFiles, setUploadingFiles] = useState({});
  const tenantId = getValues('tenant_id');
  const maxFiles = 20;

  const uploadFileToS3 = async (file, fileKey) => {
    try {
      setUploadingFiles((prev) => ({
        ...prev,
        [fileKey]: { progress: 0, uploading: true, error: null },
      }));

      const tenantSk = `firm#${tenantId}`;
      const filePath = `public/${fileKey}`;
      const fileSize = file?.size || 0;
      const s3Response = await getPresignedURLOpen(tenantId, tenantSk, filePath, fileSize, true);

      const formData = new FormData();
      Object.entries(s3Response?.data?.fields || {}).forEach(([k, v]) => formData.append(k, v));
      formData.append('file', file);

      await axiosInstance.post(s3Response?.data?.url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progress) => {
          const percent = progress.total ? Math.round((progress.loaded / progress.total) * 100) : 0;
          setUploadingFiles((prev) => ({
            ...prev,
            [fileKey]: { ...prev[fileKey], progress: percent },
          }));
        },
      });

      setUploadingFiles((prev) => {
        const next = { ...prev };
        delete next[fileKey];
        return next;
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Upload error:', err);
      setUploadingFiles((prev) => {
        const next = { ...prev };
        delete next[fileKey];
        return next;
      });
      setError(name, {
        type: 'upload_error',
        message: `Failed to upload ${file?.name}`,
      });
    }
  };

  useEffect(() => {
    const hasUploading = Object.keys(uploadingFiles || {}).length > 0;
    if (!hasUploading) {
      clearErrors(name);
    } else {
      setError(name, { type: 'uploading', message: 'Please wait for file uploads to complete' });
    }
  }, [uploadingFiles, setError, clearErrors, name]);

  return (
    <div className="w-100">
      <LabelName>
        {label}
        {required && <span className="danger-text"> *</span>}
      </LabelName>
      <div>
        <Controller
          name={name}
          control={control}
          defaultValue={[]}
          rules={
            isFormBuilder
              ? {}
              : {
                  required: required ? 'At least one file is required' : false,
                  validate: {
                    uploading: () => {
                      const hasUploading = Object.keys(uploadingFiles || {}).length > 0;
                      return !hasUploading || 'Please wait for file uploads to complete';
                    },
                  },
                }
          }
          render={({ field: { onChange, value } }) => (
            <>
              {!isFromPreview && (
                <Dropzone
                  multiple
                  onDrop={async (acceptedFiles) => {
                    if (!acceptedFiles?.length || isFormBuilder) return;

                    const currentFiles = Array.isArray(value) ? value : [];
                    const totalFiles = [...currentFiles, ...acceptedFiles];
                    if (totalFiles.length > maxFiles) {
                      addToast(
                        false,
                        toastConstant.toasterType.WARN,
                        toastConstant.header.WARNINIG,
                        toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED
                      );
                      return;
                    }

                    const userId = getValues('created_by') || 'file-upload';

                    const filesToUpload = acceptedFiles.map((file) => {
                      const fileKey = `file_manager/${tenantId}/${userId}/${uuidv4()}#$#${encodeURIComponent(file.name)}`;
                      const fileObj = {
                        display_name: file.name,
                        file_name: file.name,
                        file_path: 'public/' + fileKey,
                        file_type: file.type,
                        size: String(file.size),
                      };
                      return { fileKey, file, ...fileObj };
                    });

                    onChange([...(Array.isArray(value) ? value : []), ...filesToUpload]);

                    await Promise.all(
                      filesToUpload.map(async (f) => (f?.file?.name ? uploadFileToS3(f.file, f.fileKey) : Promise.resolve()))
                    );
                  }}
                  accept=".rar, .zip, .jpg, .jpeg, .png, .heic, .heif, .pdf, .doc, .docx, .xls, .xlsx, .txt, .mov, .wav, .mp3, .rtf, .mpeg, .mp4, .aac, .3gp, .avi, .wmv, .wma, .csv"
                >
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <section style={{ pointerEvents: isFormBuilder ? 'none' : 'auto' }}>
                      <div
                        {...getRootProps()}
                        className={`text-center d-flex flex-column justify-content-center align-items-center file-manager-uploader mb-2 p-3 border border-dashed rounded ${
                          isDragActive ? 'border-primary bg-light' : ''
                        }`}
                      >
                        <input {...getInputProps()} />
                        <i className="pi pi-cloud-upload mb-2 pointer" />
                        <p className="F-size14">
                          {isDragActive ? (
                            'Drop files here...'
                          ) : (
                            <>
                              <span style={{ color: '#3853bd', fontWeight: 600 }}>Click to Upload</span> or drag and drop
                            </>
                          )}
                        </p>
                        {Object.keys(uploadingFiles || {}).length > 0 && (
                          <small className="text-muted">
                            Uploading {Object.keys(uploadingFiles).length} file
                            {Object.keys(uploadingFiles).length > 1 ? 's' : ''}...
                          </small>
                        )}
                      </div>
                    </section>
                  )}
                </Dropzone>
              )}
              {(Array.isArray(value) ? value : [])?.map((file, i) => {
                if (!file) return null;
                const fileKey = file?.fileKey; // present for new uploads
                const fileIsUploading = Boolean(fileKey && uploadingFiles[fileKey]);
                const fileProgress = fileKey ? uploadingFiles[fileKey]?.progress ?? 0 : 0;

                const sizeNum = Number(file?.size);
                const formattedSize = !Number.isNaN(sizeNum)
                  ? sizeNum >= 1024 * 1024
                    ? (sizeNum / (1024 * 1024)).toFixed(2) + ' MB'
                    : (sizeNum / 1024).toFixed(2) + ' KB'
                  : '';

                return (
                  <div className="w-100 mt-2" key={file?.fileKey || i}>
                    <div className="d-flex w-100 justify-content-between align-items-center flex-wrap">
                      <Tag
                        value={`${file?.display_name || file?.file_name || file?.name || 'File'}${
                          formattedSize ? ' - ' + formattedSize : ''
                        }`}
                        severity="warning"
                        className="p-px-2 p-py-1 me-2"
                        style={{ cursor: 'default' }}
                      />
                      {isFromPreview ? (
                        <Button
                          role="button"
                          icon="pi pi-download"
                          tooltip="Download"
                          tooltipOptions={{ showDelay: 500 }}
                          className="p-button-rounded p-button-text"
                          onClick={async () => {
                            const link = await getSignedUrl(file);
                            console.log('link', link);
                            downloadFile(link, file.file_name);
                          }}
                        ></Button>
                      ) : (
                        <Button
                          tooltip="Delete"
                          tooltipOptions={{ showDelay: 500 }}
                          icon="pi pi-times"
                          className="p-button-rounded p-button-text text-danger"
                          onClick={async () => {
                            const files = Array.isArray(value) ? value : [];
                            onChange(files.filter((_, idx) => idx !== i));
                          }}
                          disabled={fileIsUploading}
                        />
                      )}
                    </div>
                    {fileIsUploading && <ProgressBar value={fileProgress} className="mt-1" style={{ height: '4px' }} />}
                  </div>
                );
              })}
            </>
          )}
        />
      </div>
      <SubText {...{ subtext, handleSettingsClick, item, isFormBuilder }} />
      <RequiredValidation field={name} errors={errors} />
    </div>
  );
}

export default FileUpload;
